import { numberParam } from '../types/checks.js';
import type { CheckResult, ValidationCheck } from '../types/checks.js';
import { clamp, roundTo } from '../types/geometry.js';
import { computeBrightnessInRect } from '../quality/brightness.js';
import { missingRequirementReason, notChecked, plainResult } from './support.js';

/**
 * Exposure check: overall brightness plus clipping at both ends.
 *
 * Split out from brightness on purpose — a well-exposed photo on average can
 * still have a blown-out forehead or crushed eye sockets, and both are grounds
 * for a retake.
 */
export const exposureCheck: ValidationCheck = {
  id: 'exposure',
  title: 'Экспозиция',
  description: 'Проверяет среднюю яркость, пересветы и потери в тенях.',
  kind: 'quality',
  requires: ['quality', 'pixels'],

  run: (ctx): CheckResult => {
    const missing = missingRequirementReason(ctx, ['quality']);
    if (missing) return notChecked(ctx, exposureCheck, missing, ctx.rule.severity);
    const quality = ctx.quality;
    if (!quality) return notChecked(ctx, exposureCheck, 'Метрики качества недоступны.', ctx.rule.severity);

    const minMean = numberParam(ctx.rule.params, 'minMean', 60);
    const maxMean = numberParam(ctx.rule.params, 'maxMean', 205);
    const severeLow = numberParam(ctx.rule.params, 'severeLow', 42);
    const severeHigh = numberParam(ctx.rule.params, 'severeHigh', 232);
    const maxClippedHighlights = numberParam(ctx.rule.params, 'maxClippedHighlights', 0.02);
    const maxClippedShadows = numberParam(ctx.rule.params, 'maxClippedShadows', 0.05);

    /**
     * Clip detection runs on the face when we know where it is: a white
     * background legitimately sits at 250+, and counting it as a blown highlight
     * would reject correct photos taken for documents that require a white wall.
     */
    const faceRegion = ctx.geometry
      ? {
          x: clamp(ctx.geometry.faceBox.x + ctx.geometry.faceBox.width * 0.08, 0, 1),
          y: clamp(ctx.geometry.faceBox.y + ctx.geometry.faceBox.height * 0.04, 0, 1),
          width: clamp(ctx.geometry.faceBox.width * 0.84, 0.01, 1),
          height: clamp(ctx.geometry.faceBox.height * 0.92, 0.01, 1),
        }
      : null;
    const exposure =
      ctx.pixels && faceRegion
        ? computeBrightnessInRect(ctx.pixels.luma, ctx.pixels.width, ctx.pixels.height, faceRegion, {
            shape: 'ellipse',
          })
        : quality.brightness;
    const measuredOn = ctx.pixels && faceRegion ? 'face' : 'frame';
    const { mean, clippedHighlightsRatio, clippedShadowsRatio, dynamicRange } = exposure;

    const problems: string[] = [];
    let worst: 'pass' | 'warning' | 'fail' = 'pass';
    const escalate = (level: 'warning' | 'fail'): void => {
      if (level === 'fail' || worst === 'pass') worst = level;
    };

    if (mean < severeLow) {
      problems.push(`кадр сильно недоэкспонирован (средняя яркость ${roundTo(mean, 0)})`);
      escalate('fail');
    } else if (mean < minMean) {
      problems.push(`кадр темноват (средняя яркость ${roundTo(mean, 0)})`);
      escalate('warning');
    }

    if (mean > severeHigh) {
      problems.push(`кадр пересвечен (средняя яркость ${roundTo(mean, 0)})`);
      escalate('fail');
    } else if (mean > maxMean) {
      problems.push(`кадр светлее рекомендуемого (средняя яркость ${roundTo(mean, 0)})`);
      escalate('warning');
    }

    if (clippedHighlightsRatio > maxClippedHighlights) {
      problems.push(
        `в светах потеряно ${(clippedHighlightsRatio * 100).toFixed(1).replace('.', ',')} % деталей`,
      );
      escalate(clippedHighlightsRatio > maxClippedHighlights * 3 ? 'fail' : 'warning');
    }
    if (clippedShadowsRatio > maxClippedShadows) {
      problems.push(
        `в тенях потеряно ${(clippedShadowsRatio * 100).toFixed(1).replace('.', ',')} % деталей`,
      );
      escalate(clippedShadowsRatio > maxClippedShadows * 3 ? 'fail' : 'warning');
    }

    const displayValue = `${roundTo(mean, 0).toFixed(0)} / 255`;

    return plainResult({
      id: exposureCheck.id,
      title: exposureCheck.title,
      status: worst,
      severity: ctx.rule.severity,
      value: roundTo(mean, 1),
      min: minMean,
      max: maxMean,
      message:
        worst === 'pass'
          ? 'Освещение равномерное, без пересветов и провалов в тенях.'
          : `Проблемы с освещением: ${problems.join('; ')}.`,
      requirement: `Средняя яркость ${minMean}–${maxMean}; пересветы не более ${(maxClippedHighlights * 100).toFixed(0)} %; тени не более ${(maxClippedShadows * 100).toFixed(0)} %.`,
      fixability: 'retake',
      geometry: ctx.geometry
        ? [
            {
              type: 'rect',
              coordinates: [
                roundTo(ctx.geometry.faceBox.x, 4),
                roundTo(ctx.geometry.faceBox.y, 4),
                roundTo(ctx.geometry.faceBox.width, 4),
                roundTo(ctx.geometry.faceBox.height, 4),
              ],
            },
          ]
        : undefined,
      confidence: 0.8,
      details: {
        mean: roundTo(mean, 2),
        median: roundTo(quality.brightness.median, 2),
        clippedHighlightsRatio: roundTo(clippedHighlightsRatio, 4),
        clippedShadowsRatio: roundTo(clippedShadowsRatio, 4),
        dynamicRange: roundTo(dynamicRange, 1),
        limits: { minMean, maxMean, maxClippedHighlights, maxClippedShadows },
        measuredOn,
        faceRegion,
        frameMean: roundTo(quality.brightness.mean, 1),
        displayValue,
      },
    });
  },
};
